// @ts-strict-ignore
import * as React from 'react';
import Layout from 'antd/lib/layout';
import { Content, Footer } from 'antd/lib/layout/layout';
import styles from './styles/stepStyles';
import Text from 'antd/lib/typography/Text';
import { ReleaseEventsTimelineSection } from '@/components/releaseOverview/ReleaseTimelineSection';
import { ReleaseTimelineSectionContext } from '@/components/timelineEvents/EventsTimeline';
import { ReleaseStepInactiveContext } from '@/contexts';
import { RootState } from '@/reducers/reducers';
import { FeatureSection, ReleaseStepComponentProps } from '@/typescriptHelpers';
import {
    selectApp,
    selectAutomationById,
    selectIsRunwayOrg,
    selectRelease,
    selectReleaseStep,
    selectTranslationsIntegration,
    selectVCSIntegration,
} from '@/selectors/selectors';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import { AutomationType, ReleaseStepTranslationsData, ReleaseTimelinePhase } from '@/types';
import { TranslationStrings } from '@/components/translations/TranslationStrings';
import Space from 'antd/lib/space';
import { CheckOutlined, CloseOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { runwayColors } from '@/components/styles/globalStyles';
import Select from 'antd/lib/select';
import PercentTranslationsCompleteComponent from '@/components/translations/PercentTranslationsCompleteComponent';
import PermissionedActionButton from '@/components/HOCs/PermissionedActionButton';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import { ReleaseStepUpdatable } from '@/actions/updatable';
import { UpdateTranslationsApprovalRequest } from '@/actions/release';
import { EmptyStateWrapper } from './releaseEmptyState/EmptyStateWrapper';
import IntegrationIcon from '@/components/icons/IntegrationIcon';
import { globalMessage } from '@/utils/globalMessage';
import { exportTranslationsExplainerMessage, uploadTranslationsExplainerMessage } from '@/helpers/displayHelpers';
import { tryUpdateReleaseStep } from '@/actions/releaseStep';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { getAutomationIdByType } from '@/helpers';
import { ReleaseStepOrStepRunComponentProps } from '@/components/ValidatedRouteComponents';
import TooltipInfoIcon from './helperComponents/TooltipInfoIcon';

interface State {
    workingBranchEnabled?: boolean;
}

class StepContentTranslations extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            workingBranchEnabled: null,
        };
    }

    static getDerivedStateFromProps(props: Props, state: State) {
        if (props.showBranchSelectionField && !props.releaseBranchExists && state.workingBranchEnabled === null) {
            // default to showing items from working branch when release branch doesn't exist
            return {
                ...state,
                workingBranchEnabled: true,
            };
        }

        return null;
    }

    render() {
        return (
            <Layout>
                <Content
                    style={{
                        ...styles.innerContent,
                        paddingTop: 0,
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            paddingTop: 40,
                            paddingBottom: 36,
                        }}
                    >
                        <div style={{ marginRight: 36 }}>
                            <PercentTranslationsCompleteComponent
                                workingBranchEnabled={this.state.workingBranchEnabled}
                                releaseId={this.props.releaseId}
                                workingBranchKeys={this.props.workingBranchKeys}
                                keys={this.props.keys}
                            />
                        </div>
                        <Space direction="vertical">
                            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                                {this.props.showBranchSelectionField && (
                                    <Space direction="vertical" style={{ marginRight: 24 }}>
                                        <Space>
                                            {this.props.vcsIntegrationId && (
                                                <IntegrationIcon
                                                    integrationId={this.props.vcsIntegrationId}
                                                    size={20}
                                                />
                                            )}
                                            <Text strong>Base branch</Text>
                                            <TooltipInfoIcon tooltipContent="View progress of translations against the selected branch" />
                                        </Space>
                                        <Space>
                                            <Select
                                                onChange={(value) => {
                                                    const workingBranchEnabled = value === this.props.workingBranch;

                                                    this.setState({
                                                        ...this.state,
                                                        workingBranchEnabled,
                                                    });
                                                }}
                                                defaultValue={
                                                    this.props.releaseBranchExists ?
                                                        this.props.releaseBranch
                                                    :   this.props.workingBranch
                                                }
                                                style={{ minWidth: 300 }}
                                            >
                                                <Select.Option
                                                    value={this.props.releaseBranch}
                                                    disabled={!this.props.releaseBranchExists}
                                                >
                                                    {this.props.releaseBranch}
                                                    {!this.props.releaseBranchExists && ' (not created)'}
                                                </Select.Option>
                                                <Select.Option value={this.props.workingBranch}>
                                                    {this.props.workingBranch}
                                                </Select.Option>
                                            </Select>
                                        </Space>
                                    </Space>
                                )}
                            </div>
                            {this.state.workingBranchEnabled && (
                                <Text style={{ color: runwayColors.statusYellow }}>
                                    {this.props.releaseBranchExists ?
                                        'Release branch not selected'
                                    :   'Release branch not created'}
                                </Text>
                            )}
                        </Space>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'flex-end',
                                paddingLeft: 24,
                                flexGrow: 1,
                            }}
                        ></div>
                        <div style={{ marginTop: '-60px' }}>
                            <ReleaseEventsTimelineSection
                                orgId={this.props.orgId}
                                appId={this.props.appId}
                                releaseId={this.props.releaseId}
                                stepRunId={this.props.stepRunComponentProps?.stepRunId}
                                context={ReleaseTimelineSectionContext.ReleaseStep}
                                releaseStepType={this.props.releaseStepType}
                            />
                        </div>
                    </div>
                    <TranslationStrings
                        orgId={this.props.orgId}
                        appId={this.props.appId}
                        stepRunId={this.props.stepRunId}
                        releaseId={this.props.releaseId}
                        workingBranchEnabled={
                            this.state.workingBranchEnabled === null ? false : this.state.workingBranchEnabled
                        }
                        mergeRunwayPRsEnabled={this.props.mergeRunwayPRsEnabled}
                        releaseBranchName={this.props.releaseBranch}
                        releaseStepType={this.props.releaseStepType}
                        sourceLocale={this.props.sourceLocale}
                        latestPullRequest={this.props.latestPullRequest}
                        providerState={this.props.providerState}
                        keys={this.props.keys}
                        workingBranchKeys={this.props.workingBranchKeys}
                        autoSyncTranslationsAutomationId={this.props.autoSyncTranslationsAutomationId}
                    />
                </Content>
                <EmptyStateWrapper
                    releaseStepType={this.props.releaseStepType}
                    appId={this.props.appId}
                    featureSections={[FeatureSection.TranslationCTAs]}
                    releaseId={this.props.releaseId}
                    shouldShowAsTooltip
                    customWrapperStyle={{ position: 'absolute' }}
                >
                    <Footer style={styles.footerStyle}>
                        <TranslationsStepFooter {...this.props} />
                    </Footer>
                </EmptyStateWrapper>
            </Layout>
        );
    }
}

interface TranslationsStepFooterProps extends ReleaseStepComponentProps {
    workingBranch: string;
    releaseBranch: string;
    isKickedOff: boolean;
    isApproved: boolean;
    userId: string;
    translationsAutomationIsConnected: boolean;
    stepRunId: string;
}

const TranslationsStepFooter: React.FC<TranslationsStepFooterProps> = (props) => {
    const uploadFilesIsLoading = useIsLoading(ReleaseStepUpdatable.UploadTranslationStrings, props.releaseId);
    const uploadFilesError = useError(ReleaseStepUpdatable.UploadTranslationStrings, props.releaseId);
    const uploadFilesUpdated = useUpdated(ReleaseStepUpdatable.UploadTranslationStrings, props.releaseId);
    const exportFilesIsLoading = useIsLoading(ReleaseStepUpdatable.ExportTranslationStrings, props.releaseId);
    const exportFilesError = useError(ReleaseStepUpdatable.ExportTranslationStrings, props.releaseId);
    const exportFilesUpdated = useUpdated(ReleaseStepUpdatable.ExportTranslationStrings, props.releaseId);

    const approveTranslationsIsLoading = useIsLoading(ReleaseStepUpdatable.TranslationsApproval, props.releaseId);
    const translationsApproved = useUpdated(ReleaseStepUpdatable.TranslationsApproval, props.releaseId);
    const translationsApprovalError = useError(ReleaseStepUpdatable.TranslationsApproval, props.releaseId);

    const dispatch = useDispatch();

    React.useEffect(() => {
        if (uploadFilesError) {
            globalMessage.error(uploadFilesError);
        }
    }, [uploadFilesError]);

    React.useEffect(() => {
        if (uploadFilesUpdated) {
            globalMessage.success('Localization files uploaded!');
        }
    }, [uploadFilesUpdated]);

    React.useEffect(() => {
        if (translationsApprovalError) {
            globalMessage.error(translationsApprovalError);
        }
    }, [translationsApprovalError]);

    React.useEffect(() => {
        if (translationsApproved) {
            globalMessage.success('Translations approval updated!');
        }
    }, [translationsApproved]);

    React.useEffect(() => {
        if (exportFilesError) {
            globalMessage.error(exportFilesError);
        }
    }, [exportFilesError]);

    React.useEffect(() => {
        if (exportFilesUpdated) {
            globalMessage.success('Localization files exported and PR opened!');
        }
    }, [exportFilesUpdated]);

    const onClickApproveTranslations = (isApproved: boolean) => {
        const request: UpdateTranslationsApprovalRequest = {
            isApproved: isApproved,
            userId: props.userId,
        };
        dispatch(
            tryUpdateReleaseStep(
                ReleaseStepUpdatable.TranslationsApproval,
                {
                    appId: props.appId,
                    orgId: props.orgId,
                    releaseId: props.releaseId,
                    stepRunId: props.stepRunId,
                },
                request,
                props.releaseId,
            ),
        );
    };

    const renderUploadExportTranslationsCTAs = () => {
        const hasReleaseBranch = !!props.releaseBranch;
        const isReleaseBranchSameAsWorkingBranch = props.workingBranch === props.releaseBranch;
        const releaseBranchIsDefaultBranch = hasReleaseBranch && props.isKickedOff;
        const defaultUploadContent = () => {
            const branchName = releaseBranchIsDefaultBranch ? props.releaseBranch : props.workingBranch;
            return (
                <Space align="center">
                    <Text>Upload strings from &#39;{branchName}&#39; for translation</Text>
                    <TooltipInfoIcon tooltipContent={uploadTranslationsExplainerMessage()} />
                </Space>
            );
        };
        const defaultExportContent = () => {
            const branchName = releaseBranchIsDefaultBranch ? props.releaseBranch : props.workingBranch;
            return (
                <Space align="center">
                    <Text>Export strings to &#39;{branchName}&#39;</Text>
                    <TooltipInfoIcon tooltipContent={exportTranslationsExplainerMessage()} />
                </Space>
            );
        };

        const onClickUploadTranslations = (useWorkingBranch: boolean) => {
            dispatch(
                tryUpdateReleaseStep(
                    ReleaseStepUpdatable.UploadTranslationStrings,
                    {
                        appId: props.appId,
                        orgId: props.orgId,
                        releaseId: props.releaseId,
                        stepRunId: props.stepRunId,
                    },
                    {
                        branchName: useWorkingBranch ? props.workingBranch : props.releaseBranch,
                    },
                    props.releaseId,
                ),
            );
        };

        const onClickExportTranslations = (useWorkingBranch: boolean) => {
            dispatch(
                tryUpdateReleaseStep(
                    ReleaseStepUpdatable.ExportTranslationStrings,
                    {
                        appId: props.appId,
                        orgId: props.orgId,
                        releaseId: props.releaseId,
                        stepRunId: props.stepRunId,
                    },
                    {
                        branchName: useWorkingBranch ? props.workingBranch : props.releaseBranch,
                    },
                    props.releaseId,
                ),
            );
        };

        if (isReleaseBranchSameAsWorkingBranch || !hasReleaseBranch) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <PermissionedActionButton
                        disabled={!props.translationsAutomationIsConnected}
                        actionType={RunwayUserAction.UAImportExportTranslationStrings}
                        releaseId={props.releaseId}
                        onClick={() => onClickUploadTranslations(!releaseBranchIsDefaultBranch)}
                        loading={uploadFilesIsLoading}
                    >
                        {defaultUploadContent()}
                    </PermissionedActionButton>
                    <PermissionedActionButton
                        disabled={!props.translationsAutomationIsConnected}
                        actionType={RunwayUserAction.UAImportExportTranslationStrings}
                        releaseId={props.releaseId}
                        onClick={() => onClickExportTranslations(!releaseBranchIsDefaultBranch)}
                        loading={exportFilesIsLoading}
                    >
                        {defaultExportContent()}
                    </PermissionedActionButton>
                </div>
            );
        }

        const altBranchName = releaseBranchIsDefaultBranch ? props.workingBranch : props.releaseBranch;

        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <PermissionedActionButton
                    actionType={RunwayUserAction.UAImportExportTranslationStrings}
                    disabled={!props.translationsAutomationIsConnected}
                    menu={{
                        items: [
                            {
                                key: 'upload-alternate',
                                label: `Upload localization files from '${altBranchName}' for translation`,
                                icon: <UploadOutlined style={{ color: runwayColors.primary }} />,
                                onClick: () => onClickUploadTranslations(releaseBranchIsDefaultBranch),
                            },
                        ],
                    }}
                    buttonsRender={([leftButton, rightButton]) => [
                        React.cloneElement(leftButton as React.ReactElement<any, string>, {
                            loading: uploadFilesIsLoading,
                        }),

                        React.cloneElement(rightButton as React.ReactElement<any, string>, {
                            loading: uploadFilesIsLoading,
                        }),
                    ]}
                    icon={<UploadOutlined style={{ color: runwayColors.primary }} />}
                    trigger={['click']}
                    loading={uploadFilesIsLoading}
                    onClick={() => onClickUploadTranslations(!releaseBranchIsDefaultBranch)}
                >
                    {defaultUploadContent()}
                </PermissionedActionButton>
                <PermissionedActionButton
                    actionType={RunwayUserAction.UAImportExportTranslationStrings}
                    disabled={!props.translationsAutomationIsConnected}
                    menu={{
                        items: [
                            {
                                key: 'export-alternate',
                                label: `Export localization files to '${altBranchName}'`,
                                icon: <DownloadOutlined style={{ color: runwayColors.primary }} />,
                                onClick: () => onClickExportTranslations(releaseBranchIsDefaultBranch),
                            },
                        ],
                    }}
                    buttonsRender={([leftButton, rightButton]) => [
                        React.cloneElement(leftButton as React.ReactElement<any, string>, {
                            loading: exportFilesIsLoading,
                        }),

                        React.cloneElement(rightButton as React.ReactElement<any, string>, {
                            loading: exportFilesIsLoading,
                        }),
                    ]}
                    trigger={['click']}
                    loading={exportFilesIsLoading}
                    onClick={() => onClickExportTranslations(!releaseBranchIsDefaultBranch)}
                    icon={<DownloadOutlined style={{ color: runwayColors.primary }} />}
                >
                    {defaultExportContent()}
                </PermissionedActionButton>
            </div>
        );
    };
    return (
        <Space size="large" align="center">
            {renderUploadExportTranslationsCTAs()}
            <PermissionedActionButton
                key="approve"
                releaseId={props.releaseId}
                actionType={RunwayUserAction.UAApproveTranslations}
                onClick={() => onClickApproveTranslations(!props.isApproved)}
                icon={
                    props.isApproved ? <CloseOutlined style={{ color: runwayColors.statusRed }} /> : <CheckOutlined />
                }
                type={props.isApproved ? 'default' : 'primary'}
                loading={approveTranslationsIsLoading}
            >
                {props.isApproved ? 'Rescind approval' : 'Approve translations'}
            </PermissionedActionButton>
        </Space>
    );
};

StepContentTranslations.contextType = ReleaseStepInactiveContext;
const mapProps = (state: RootState, ownProps: ReleaseStepOrStepRunComponentProps) => {
    const { orgId, appId, releaseId, releaseStepType } = ownProps;

    const release = selectRelease(state, releaseId);
    const app = selectApp(state, appId);
    const releaseStep = selectReleaseStep(state, releaseId, releaseStepType);
    const stepRunId = releaseStep.id;
    const vcsIntegrationId = selectVCSIntegration(state, appId)?.id;
    const stepStatus = releaseStep.status;
    const appPlatform = app.appSettings.platform;
    const platform = app.appSettings.platform;
    const appStoreType = app.appSettings.appStoreType;
    const userId = state.user.id;

    const stepData = releaseStep.data as ReleaseStepTranslationsData;
    const isApproved = stepData.isApproved;

    const version = release.version;
    const isRunwayOrg = selectIsRunwayOrg(state);

    const releaseType = release.type;
    const isKickedOff = stepData.isKickedOff;

    const rollbackTargetVersionInfo = release.rollbackTargetVersionInfo;

    const workingBranch = stepData.workingBranch;
    const releaseBranch = stepData.releaseBranch;
    const releaseBranchExists = stepData.doesBranchExist;

    const translationsData = stepData.translationsData;
    const workingBranchKeys = translationsData?.workingBranchKeys ?? [];
    const keys = translationsData?.keys ?? [];

    const showBranchSelectionField =
        !!workingBranch && workingBranch !== releaseBranch && release.timelinePhase === ReleaseTimelinePhase.Current;

    const translationsAutomation = selectTranslationsIntegration(state, appId);
    const translationsAutomationIsConnected = !!translationsAutomation;

    const sourceLocale = translationsData?.sourceLocale ?? '';
    const latestPullRequest = translationsData?.latestPullRequest;
    const providerState = translationsData?.providerState;

    const autoSyncTranslationsAutomationId = getAutomationIdByType(
        releaseStep.automationRuns,
        AutomationType.AutoSyncTranslations,
    );

    const mergeRunwayPRsAutomationId = getAutomationIdByType(
        releaseStep.automationRuns,
        AutomationType.AutoMergeRunwayPRs,
    );

    const mergeRunwayPRsEnabled = selectAutomationById(state, appId, mergeRunwayPRsAutomationId)?.enabled;

    return {
        orgId,
        appId,
        releaseId,
        stepRunId,
        autoSyncTranslationsAutomationId,
        workingBranchKeys,
        sourceLocale,
        latestPullRequest,
        providerState,
        keys,
        stepRunComponentProps: ownProps.stepRunComponentProps,
        releaseStepType,
        appPlatform,
        stepStatus,
        platform,
        appStoreType,
        version,
        isRunwayOrg,
        releaseType,
        rollbackTargetVersionInfo,
        workingBranch,
        releaseBranch,
        releaseBranchExists,
        showBranchSelectionField,
        isKickedOff,
        mergeRunwayPRsEnabled,
        userId,
        isApproved,
        translationsAutomationIsConnected,
        vcsIntegrationId,
    };
};

const mapDispatch = () => {
    return {};
};
const connector = connect(mapProps, mapDispatch);

type Props = ConnectedProps<typeof connector>;

export default connector(StepContentTranslations);
